export type BlindBindErrorCode =
  | "connection_failed"
  | "offline"
  | "host_unavailable"
  | "busy"
  | "rate_limited"
  | "identity_mismatch"
  | "handshake_failed"
  | "protocol_error"
  | "closed";

const temporaryCodes = new Set<BlindBindErrorCode>([
  "connection_failed",
  "offline",
  "host_unavailable",
  "busy",
  "rate_limited",
]);

export class BlindBindError extends Error {
  readonly code: BlindBindErrorCode;
  readonly temporary: boolean;
  readonly retryAfterMs: number | undefined;
  override readonly cause: unknown;

  constructor(code: BlindBindErrorCode, message: string, options: { cause?: unknown; retryAfterMs?: number } = {}) {
    super(message);
    this.name = "BlindBindError";
    this.code = code;
    this.temporary = temporaryCodes.has(code);
    this.retryAfterMs = options.retryAfterMs;
    this.cause = options.cause;
  }
}

export function asBlindBindError(error: unknown, fallback: BlindBindErrorCode = "connection_failed"): BlindBindError {
  if (error instanceof BlindBindError) return error;
  if (error instanceof DOMException && error.name === "AbortError") {
    return new BlindBindError("closed", "Blind-bind operation was aborted", { cause: error });
  }
  return new BlindBindError(fallback, error instanceof Error ? error.message : String(error), { cause: error });
}

export function errorForClose(code: number, reason: string): BlindBindError {
  const message = reason || "Blind-bind WebSocket closed";
  switch (code) {
    case 4400:
      return new BlindBindError("protocol_error", message);
    case 4404:
      return new BlindBindError("offline", message);
    case 4429:
      return new BlindBindError("busy", message);
    case 4503:
      return new BlindBindError("host_unavailable", message);
    case 1000:
      return new BlindBindError("closed", message);
    default:
      return new BlindBindError("connection_failed", message);
  }
}
